import { ReportPeriod, ReportType } from "@/components/common/report-chip";
import { Checkbox } from "@/styles/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/styles/ui/popover";
import { ChevronDown } from "lucide-react";
import { ToolbarButton } from "./toolbar-button";

const TYPE_OPTIONS: { value: ReportType; label: string }[] = [
  { value: "CONSOLIDATED", label: "연결" },
  { value: "SEPARATE", label: "별도" },
];

const PERIOD_OPTIONS: { value: ReportPeriod; label: string }[] = [
  { value: "Q1", label: "1분기" },
  { value: "Q2", label: "반기" },
  { value: "Q3", label: "3분기" },
  { value: "Q4", label: "사업보고서" },
];

interface ReportTypeFilterProps {
  selectedTypes: Set<ReportType>;
  selectedPeriods: Set<ReportPeriod>;
  onToggleType: (type: ReportType) => void;
  onTogglePeriod: (period: ReportPeriod) => void;
}

// Figma 5577:331714 "기능 Dropdown" — 체크박스 20px(연한 테두리) + 14px Regular.
// 토글은 행 클릭으로 처리하므로 체크박스는 시각 표시만 하고 포커스/포인터를 받지 않는다.
const OptionRow = ({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full items-center gap-1.5 rounded-md p-1 text-left outline-none transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:outline-none"
  >
    <Checkbox
      checked={checked}
      tabIndex={-1}
      aria-hidden
      className="pointer-events-none size-5 rounded-[4px] border-neutral-300 shadow-none focus-visible:ring-0"
    />
    <span className="label-1-normal-regular text-label-normal">{label}</span>
  </button>
);

// ============================================
// ReportTypeFilter
// "종류" 필터 — 재무제표 유형(연결/별도)과 보고 기간(분기/반기/사업)의 다중 선택.
// ============================================

// 선택된 유형/기간을 Figma 순서(연결/별도 → 1분기/반기/3분기/사업보고서)대로 나열.
// 버튼 라벨이 좁아 다 못 들어가면 ToolbarButton의 truncate로 말줄임 처리된다.
const getSelectedLabel = (
  selectedTypes: Set<ReportType>,
  selectedPeriods: Set<ReportPeriod>,
) => {
  const labels = [
    ...TYPE_OPTIONS.filter((o) => selectedTypes.has(o.value)),
    ...PERIOD_OPTIONS.filter((o) => selectedPeriods.has(o.value)),
  ].map((o) => o.label);
  return labels.length > 0 ? labels.join(", ") : "종류";
};

export const ReportTypeFilter = ({
  selectedTypes,
  selectedPeriods,
  onToggleType,
  onTogglePeriod,
}: ReportTypeFilterProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <ToolbarButton
        className="w-[140px] focus-visible:outline-none"
        spread
        rightIcon={<ChevronDown className="size-4" />}
      >
        {getSelectedLabel(selectedTypes, selectedPeriods)}
      </ToolbarButton>
    </PopoverTrigger>
    <PopoverContent align="start" className="flex w-[180px] flex-col gap-1 p-2">
      {TYPE_OPTIONS.map((option) => (
        <OptionRow
          key={option.value}
          checked={selectedTypes.has(option.value)}
          label={option.label}
          onToggle={() => onToggleType(option.value)}
        />
      ))}
      <div className="my-1 h-px w-full bg-line-normal" />
      {PERIOD_OPTIONS.map((option) => (
        <OptionRow
          key={option.value}
          checked={selectedPeriods.has(option.value)}
          label={option.label}
          onToggle={() => onTogglePeriod(option.value)}
        />
      ))}
    </PopoverContent>
  </Popover>
);
